import { useState } from 'react';
import { useStore } from '@/store/useStore';
import { Users, UserSquare2, Plus, ListTree } from 'lucide-react';
import { cn } from '@/utils/cn';

// Вкладки «Филиалы» здесь БОЛЬШЕ НЕТ: она заводила филиал со случайным id, не связанный
// с городом в Битриксе (сделки к такому филиалу не приезжали). Филиалы — на экране «Филиалы».
// Вкладки «Bitrix24» тоже больше нет: это была нерабочая заглушка MVP (весь обмен — TODO),
// а её кнопка «Проверить связь» слала полноправный вебхук из браузера. Реальная интеграция —
// серверная. Убрано в аудите безопасности.
type Tab = 'employees' | 'teams' | 'lists';

export function DirectoryPage() {
  const [tab, setTab] = useState<Tab>('employees');
  const { branches, employees, teams, sources, occasions, game_formats, refusal_reasons, setModal } = useStore();

  const tabs: { key: Tab; label: string; icon: typeof Users; count?: number }[] = [
    { key: 'employees', label: 'Сотрудники', icon: Users, count: employees.length },
    { key: 'teams', label: 'Команды', icon: UserSquare2, count: teams.length },
    { key: 'lists', label: 'Списки', icon: ListTree },
  ];

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <nav className="w-[230px] border-r border-line bg-surface-1 flex flex-col flex-shrink-0">
        <div className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-wider text-ink-3 font-bold">
          Справочники
        </div>
        <div className="px-2 space-y-0.5 flex-1 overflow-y-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'w-full flex items-center gap-2.5 h-9 px-3 rounded-lg text-sm font-medium transition-all',
                  active
                    ? 'bg-accent text-white shadow-soft'
                    : 'text-ink-2 hover:bg-surface-2 hover:text-ink-1',
                )}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{t.label}</span>
                {t.count != null && (
                  <span className={cn(
                    'text-[10px] px-1.5 h-4 min-w-[18px] rounded-full grid place-items-center font-bold tabular-nums',
                    active ? 'bg-white/25 text-white' : 'bg-ink-3/15 text-ink-2',
                  )}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="p-3 border-t border-line text-[10px] text-ink-3">
          v1.0.0 · +ERP
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'employees' && (
          <List title="Сотрудники" items={employees.map((e) => ({
            id: e.id, name: e.full_name, sub: branches.find((b) => b.id === e.branch_id)?.name,
            chips: [e.role ?? '—', e.is_active ? 'активен' : 'неактивен'],
          }))} onAdd={() => setModal({ kind: 'employee', id: null })} onEdit={(id) => setModal({ kind: 'employee', id })} />
        )}
        {tab === 'teams' && (
          <List title="Команды" items={teams.map((t) => ({
            id: t.id, name: t.name, color: t.color,
            sub: branches.find((b) => b.id === t.branch_id)?.name,
            chips: [`${t.member_ids.length} чел.`, t.is_archived ? 'архив' : 'активна'],
          }))} onAdd={() => setModal({ kind: 'team', id: null })} onEdit={(id) => setModal({ kind: 'team', id })} />
        )}
        {tab === 'lists' && (
          <div className="space-y-4 max-w-2xl">
            <h2 className="text-base font-semibold">Справочники-списки</h2>
            <SimpleList title="Источники" items={sources} />
            <SimpleList title="Поводы" items={occasions} />
            <SimpleList title="Форматы игры" items={game_formats} />
            <SimpleList title="Причины отказа" items={refusal_reasons} />
            <p className="text-[11px] text-ink-3">
              ℹ После подключения Bitrix24 эти справочники будут синхронизироваться с CRM_STATUS / UF_CRM_*.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function List({ title, items, onAdd, onEdit }: {
  title: string;
  items: { id: string; name: string; sub?: string; color?: string; chips?: string[] }[];
  onAdd: () => void;
  onEdit: (id: string) => void;
}) {
  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <button className="btn-primary" onClick={onAdd}>
          <Plus className="w-4 h-4" /> Добавить
        </button>
      </div>
      {items.length === 0 ? (
        <div className="panel p-6 text-center text-sm text-ink-3">Пусто. Нажмите «Добавить».</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <button key={it.id} onClick={() => onEdit(it.id)} className="panel hover:border-accent w-full p-3 flex items-center gap-3 text-left transition-colors">
              {it.color && <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: it.color }} />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-1 truncate">{it.name}</div>
                {it.sub && <div className="text-[11px] text-ink-3">{it.sub}</div>}
              </div>
              {it.chips && (
                <div className="flex gap-1 flex-shrink-0">
                  {it.chips.map((c, i) => <span key={i} className="chip">{c}</span>)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SimpleList({ title, items }: { title: string; items: { id: string; name: string; is_active?: boolean }[] }) {
  return (
    <div className="panel p-3">
      <div className="text-xs uppercase tracking-wider text-ink-3 font-semibold mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-ink-3 italic">пусто</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((i) => (
            <span key={i.id} className={cn('chip', i.is_active === false && 'opacity-50')}
              title={i.is_active === false ? 'Убрано в Битриксе — не предлагается для новых записей, но остаётся на тех, где уже проставлено' : undefined}>
              {i.name}{i.is_active === false ? ' (архив)' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
